import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type CSSProperties,
} from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { WidgetFrame } from "../components/WidgetFrame";
import { SwarmMoon, StatusDot, StatusPill, Btn, Icon, I } from "../kit";
import { tokenSummary, compactCount } from "../lib/format";
import {
  getSession,
  getSessionEvents,
  sendSessionMessage,
  stopSession,
  type Session,
  type SessionEvent,
} from "../lib/api";
import "../session.css";

const POLL_MS = 3_000;

// ─── session-status widget data ─────────────────────────────────────
// Client-side mirror of sessionStatusTool's structuredContent builder in
// infra/lib/lambdas/mcp/server.ts (sessionView + renderTranscriptEvent in
// infra/lib/lambdas/mcp/transcript.ts) — the embedded widget reads this
// exact shape. Keep the two in sync.

const SESSION_STATUS_LINE_COUNT = 10;

function widgetContentToText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  const parts: string[] = [];
  for (const raw of content as { type?: string; text?: string; name?: string }[]) {
    if (raw && raw.type === "text" && typeof raw.text === "string") {
      parts.push(raw.text);
    } else if (raw && raw.type === "tool_use") {
      parts.push(`⚙ ${raw.name || "tool"}`);
    } else if (raw && raw.type) {
      parts.push(`[${raw.type}]`);
    }
  }
  return parts.join(" ").trim();
}

function widgetEventLine(ev: SessionEvent): string | null {
  if (ev.kind !== "transcript") return `[${ev.kind}] ${ev.payload}`.trim();
  let parsed: { type?: string; message?: { content?: unknown } };
  try {
    parsed = JSON.parse(ev.payload);
  } catch {
    return null; // partial/corrupt line — skip
  }
  if (!parsed || typeof parsed !== "object") return null;
  if (parsed.type === "user" || parsed.type === "assistant") {
    const text = widgetContentToText(parsed.message?.content);
    return text ? `${parsed.type}: ${text}` : null;
  }
  return null; // internals (summary, system, …)
}

function buildSessionStatusData(
  session: Session,
  events: SessionEvent[]
): Record<string, unknown> {
  // Last N readable lines: scan newest-first, restore chronological order.
  const lines: string[] = [];
  for (let i = events.length - 1; i >= 0; i--) {
    const line = widgetEventLine(events[i]);
    if (!line) continue;
    lines.push(`[${events[i].seq}] ${line}`);
    if (lines.length >= SESSION_STATUS_LINE_COUNT) break;
  }
  lines.reverse();
  return {
    sessionId: session.sessionId,
    agent: session.agent,
    machineId: session.machineId,
    status: session.status,
    startedAt: session.startedAt,
    endedAt: session.endedAt ?? null,
    errorMessage: session.errorMessage ?? null,
    lastSeq: session.lastSeq ?? 0,
    // Phase 5: token counters + needs-human flag for the widget.
    needsHuman: session.needsHuman ?? false,
    inputTokens: session.inputTokens ?? 0,
    outputTokens: session.outputTokens ?? 0,
    cacheReadTokens: session.cacheReadTokens ?? 0,
    cacheWriteTokens: session.cacheWriteTokens ?? 0,
    lines,
  };
}

// ─── transcript row type-language (phase 7 Track B) ─────────────────
// Ported from design/project/lib/session.jsx — the ROW registry: one
// glyph + spine color per row kind. Raw Claude Code JSONL transcript
// events parse into typed rows; tool_use/tool_result pairs collapse into
// a single tool row with a real (event-timestamp) duration.

type RowKind = "system" | "user" | "assistant" | "thinking" | "tool";

const ROWMETA: Record<RowKind, { label: string; spine: string; glyph: string }> = {
  system: { label: "system", spine: "var(--color-fg-5)", glyph: I.dot },
  user: { label: "you", spine: "var(--color-accent)", glyph: I.user },
  assistant: { label: "agent", spine: "var(--color-fg-3)", glyph: I.bot },
  tool: { label: "tool", spine: "var(--color-accent-fg)", glyph: I.wrench },
  thinking: { label: "thinking", spine: "var(--color-warn)", glyph: I.zap },
};

interface TxRowData {
  key: string;
  kind: RowKind;
  ts: string;
  text?: string;
  /** Untruncated source for system rows (title tooltip). */
  full?: string;
  // tool rows
  name?: string;
  args?: string;
  out?: string;
  isError?: boolean;
  durMs?: number;
  done?: boolean;
}

interface ContentBlock {
  type?: string;
  text?: string;
  thinking?: string;
  id?: string;
  name?: string;
  input?: unknown;
  tool_use_id?: string;
  content?: unknown;
  is_error?: boolean;
}

function compactJson(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function toolResultText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return content == null ? "" : compactJson(content);
  const parts: string[] = [];
  for (const c of content as ContentBlock[]) {
    if (c && c.type === "text" && typeof c.text === "string") parts.push(c.text);
    else if (c && c.type) parts.push(`[${c.type}]`);
  }
  return parts.join("\n");
}

function buildRows(events: SessionEvent[]): TxRowData[] {
  const rows: TxRowData[] = [];
  const toolById = new Map<string, TxRowData>();
  for (const ev of events) {
    if (ev.kind !== "transcript") {
      rows.push({
        key: `e${ev.seq}`,
        kind: "system",
        ts: ev.ts,
        text: `[${ev.kind}] ${ev.payload}`.trim(),
      });
      continue;
    }
    let parsed: { type?: string; subtype?: string; summary?: string; message?: { content?: unknown } };
    try {
      parsed = JSON.parse(ev.payload);
    } catch {
      // partial/raw line — keep it visible, as before
      rows.push({ key: `e${ev.seq}`, kind: "system", ts: ev.ts, text: ev.payload.slice(0, 200), full: ev.payload.slice(0, 1000) });
      continue;
    }
    if (!parsed || typeof parsed !== "object") continue;
    if (parsed.type === "user" || parsed.type === "assistant") {
      const kind: RowKind = parsed.type;
      const content = parsed.message?.content;
      if (typeof content === "string") {
        if (content.trim())
          rows.push({ key: `e${ev.seq}`, kind, ts: ev.ts, text: content });
        continue;
      }
      if (!Array.isArray(content)) {
        rows.push({
          key: `e${ev.seq}`,
          kind: "system",
          ts: ev.ts,
          text: JSON.stringify(parsed).slice(0, 200),
          full: JSON.stringify(parsed).slice(0, 1000),
        });
        continue;
      }
      const texts: string[] = [];
      (content as ContentBlock[]).forEach((c, bi) => {
        if (!c || typeof c !== "object") return;
        if (c.type === "text" && typeof c.text === "string") {
          if (c.text.trim()) texts.push(c.text);
        } else if (c.type === "thinking" && typeof c.thinking === "string") {
          rows.push({ key: `e${ev.seq}.${bi}`, kind: "thinking", ts: ev.ts, text: c.thinking });
        } else if (c.type === "tool_use") {
          const row: TxRowData = {
            key: `e${ev.seq}.${bi}`,
            kind: "tool",
            ts: ev.ts,
            name: typeof c.name === "string" && c.name ? c.name : "tool",
            args: compactJson(c.input),
            done: false,
          };
          rows.push(row);
          if (typeof c.id === "string") toolById.set(c.id, row);
        } else if (c.type === "tool_result") {
          const target =
            typeof c.tool_use_id === "string" ? toolById.get(c.tool_use_id) : undefined;
          const out = toolResultText(c.content);
          if (target) {
            target.out = out;
            target.done = true;
            target.isError = !!c.is_error;
            const t0 = Date.parse(target.ts);
            const t1 = Date.parse(ev.ts);
            if (Number.isFinite(t0) && Number.isFinite(t1) && t1 >= t0)
              target.durMs = t1 - t0;
          } else {
            rows.push({
              key: `e${ev.seq}.${bi}`,
              kind: "tool",
              ts: ev.ts,
              name: "tool result",
              out,
              done: true,
              isError: !!c.is_error,
            });
          }
        } else if (c.type) {
          texts.push(`[${c.type}]`);
        }
      });
      if (texts.length)
        rows.push({ key: `e${ev.seq}.t`, kind, ts: ev.ts, text: texts.join("\n\n") });
    } else if (parsed.type === "system") {
      rows.push({
        key: `e${ev.seq}`,
        kind: "system",
        ts: ev.ts,
        text:
          typeof parsed.subtype === "string" && parsed.subtype
            ? `system · ${parsed.subtype}`
            : "system",
        full: ev.payload.slice(0, 1000),
      });
    } else if (parsed.type === "summary") {
      rows.push({
        key: `e${ev.seq}`,
        kind: "system",
        ts: ev.ts,
        text: typeof parsed.summary === "string" ? parsed.summary : "summary",
        full: ev.payload.slice(0, 1000),
      });
    } else {
      rows.push({
        key: `e${ev.seq}`,
        kind: "system",
        ts: ev.ts,
        text: JSON.stringify(parsed).slice(0, 200),
        full: ev.payload.slice(0, 1000),
      });
    }
  }
  return rows;
}

// ─── formatting helpers ─────────────────────────────────────────────

function fmtTime(iso: string): string {
  const t = new Date(iso);
  return Number.isFinite(t.getTime())
    ? t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    : "";
}

function fmtWhen(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}`;
}

function fmtDur(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = ms / 1000;
  if (s < 10) return `${s.toFixed(1)}s`;
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  return `${m}m${String(Math.round(s % 60)).padStart(2, "0")}s`;
}

function fmtElapsed(start: string, end: string | null, live: boolean, now: number): string | null {
  const t0 = Date.parse(start);
  if (!Number.isFinite(t0)) return null;
  const t1 = end ? Date.parse(end) : live ? now : NaN;
  if (!Number.isFinite(t1)) return null;
  let s = Math.max(0, Math.floor((t1 - t0) / 1000));
  const h = Math.floor(s / 3600);
  s -= h * 3600;
  const m = Math.floor(s / 60);
  s -= m * 60;
  if (h > 0) return `${h}h${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

function sessionPill(s: Session) {
  switch (s.status) {
    case "starting":
      return <StatusPill status="queued">starting</StatusPill>;
    case "running":
      return <StatusPill status="streaming" />;
    case "error":
      return <StatusPill status="failed">error</StatusPill>;
    default:
      return <StatusPill status="ended" />;
  }
}

/** CSS-breakpoint mirror so the single WidgetFrame mounts in the right column. */
function useDesktop(): boolean {
  const [is, setIs] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 900px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const fn = (e: MediaQueryListEvent) => setIs(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return is;
}

// ─── transcript row ─────────────────────────────────────────────────

function TxRow({
  row,
  agent,
  last,
  working,
}: {
  row: TxRowData;
  agent: string;
  last: boolean;
  working: boolean;
}) {
  const [open, setOpen] = useState(false);
  const meta = ROWMETA[row.kind];
  const time = fmtTime(row.ts);

  if (row.kind === "system") {
    return (
      <div className="sv-row-system">
        <span title={row.full ?? row.ts}>
          <span className="sv-sys-rule" />
          <span className="sv-sys-text">{row.text}</span>
          <span className="sv-sys-rule" />
        </span>
      </div>
    );
  }

  const label = row.kind === "assistant" ? agent : meta.label;
  const spineVars = { "--sv-spine": meta.spine } as CSSProperties;
  const longOut = !!row.out && (row.out.length > 90 || row.out.includes("\n"));

  return (
    <div className="sv-row" style={spineVars}>
      <div className="sv-gutter">
        <span className="sv-glyph">
          <Icon d={meta.glyph} size={12} />
        </span>
        {!last && <span className="sv-spine" />}
      </div>
      <div className="sv-rowbody">
        <div className="sv-rowhead">
          <span className="mono sv-rowlabel">{label}</span>
          <span className="mono sv-rowtime" title={row.ts}>
            {time}
          </span>
        </div>

        {row.kind === "user" && <div className="sv-bubble-user">{row.text}</div>}

        {row.kind === "assistant" && (
          <div className="sv-msg">
            {row.text}
            {working && last && <span className="sv-cursor" />}
          </div>
        )}

        {row.kind === "thinking" && (
          <div>
            <button type="button" className="sv-think-btn" onClick={() => setOpen((o) => !o)}>
              <Icon d={open ? I.chevD : I.chevR} size={13} /> Thought process
            </button>
            {open && <div className="sv-think-body">{row.text}</div>}
          </div>
        )}

        {row.kind === "tool" && (
          <div className="sv-tool">
            <button type="button" className="sv-tool-head" onClick={() => setOpen((o) => !o)}>
              <span className="sv-tool-name">
                <Icon d={I.wrench} size={12} /> {row.name}
              </span>
              {!row.done && working ? (
                <StatusPill status="running" />
              ) : row.done ? (
                <span className={"sv-tool-stat" + (row.isError ? " sv-tool-stat--err" : "")}>
                  <Icon d={row.isError ? I.x : I.check} size={11} />
                  {row.durMs != null ? fmtDur(row.durMs) : row.isError ? "error" : "done"}
                </span>
              ) : (
                <span className="sv-tool-stat sv-tool-stat--idle">no result</span>
              )}
              <span className="sv-tool-chev">
                <Icon d={open ? I.chevD : I.chevR} size={14} />
              </span>
            </button>
            {open ? (
              <div className="sv-tool-detail">
                {row.args ? <div className="sv-tool-args">{row.args}</div> : null}
                {row.out != null && row.out !== "" ? (
                  <div className="sv-tool-out">{row.out}</div>
                ) : null}
              </div>
            ) : row.out ? (
              <div className="sv-tool-preview">
                {row.out.split("\n")[0]}
                {longOut ? " …" : ""}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── pinned at-a-glance block ───────────────────────────────────────
// Remote approval bar — when the daemon flags needsHuman, the session is
// blocked on a prompt the auto-answer corpus couldn't (or wasn't allowed
// to) handle. send_message types into the live PTY, so menu answers are
// just keystrokes: "1"/"2"/"3"/"y" + Enter. Option 2 on tool-approval
// menus is "don't ask again for this tool here" — the sticky choice.
function NeedsHumanBar({
  sessionId,
  screen,
}: {
  sessionId: string;
  screen?: string;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [custom, setCustom] = useState("");

  const respond = async (keys: string, label: string) => {
    if (busy) return;
    setBusy(label);
    try {
      await sendSessionMessage(sessionId, keys);
      setSent(label);
    } catch {
      setSent(null);
    } finally {
      setBusy(null);
    }
  };

  // The stored screen is ANSI-stripped but carries \r framing AND, because
  // the TUI positions text with cursor moves rather than spaces, words run
  // together once stripped. Clean it down to the meaningful lines: drop the
  // ───── separators, lone spinner glyphs, and the status footer, keep the
  // tail (2026-06-11 — the raw dump was unreadable).
  const cleaned = (screen || "")
    .replace(/\r/g, "\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(
      (l) =>
        l &&
        !/^[─—-]+$/.test(l) && // separator rules
        !/^[✻✶✳✽✢*∗·]+$/.test(l) // lone spinner glyphs
    )
    .slice(-12)
    .join("\n");

  // Classify the screen so the header tells the human WHAT they're looking at
  // — Matt's report: "I can't tell that it's actually stuck from the
  // messaging." A real prompt has numbered options; a "finished/idle" screen
  // has the post-turn chrome (a hung Stop hook, "Brewed for…", an idle caret)
  // and is almost always a stale flag, not a question.
  const hasPrompt =
    /\b1\s*[.)·]\s*(yes|allow|approve)/i.test(cleaned) ||
    /\(\s*y\s*\/\s*n\s*\)/i.test(cleaned) ||
    /don.?t\s*ask\s*again/i.test(cleaned);
  const looksFinished =
    !hasPrompt &&
    /running\s*stop\s*hook|brewed\s*for|nebulizing|esc\s*to\s*rewind|❯/i.test(
      screen || ""
    );

  const header = hasPrompt
    ? "Waiting on a permission prompt — answer it:"
    : looksFinished
      ? "This session looks finished (idle at the prompt) — it isn't asking for anything. The flag is likely stale; stopping it is safe."
      : "The agent went quiet. Last screen below — answer if it's waiting, or stop the session.";

  return (
    <div className={`sv-needs-bar${looksFinished ? " finished" : ""}`}>
      <div className="sv-needs-head">{header}</div>
      {cleaned && <pre className="sv-needs-screen mono">{cleaned}</pre>}
      <div className="sv-needs-actions">
        <Btn size="sm" variant="primary" disabled={!!busy} onClick={() => respond("2", "2")}>
          2 · Yes, don't ask again
        </Btn>
        <Btn size="sm" disabled={!!busy} onClick={() => respond("1", "1")}>
          1 · Yes, once
        </Btn>
        <Btn size="sm" variant="danger" disabled={!!busy} onClick={() => respond("3", "3")}>
          3 · No
        </Btn>
        <input
          className="input sv-needs-input mono"
          placeholder="custom keys…"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
        />
        <Btn
          size="sm"
          disabled={!!busy || !custom.trim()}
          onClick={() => respond(custom.trim(), "custom")}
        >
          Send
        </Btn>
      </div>
      <span className="hint">
        {busy
          ? "sending…"
          : sent
            ? `sent "${sent}" — the session should move within a few seconds`
            : "answers type directly into the live session"}
      </span>
    </div>
  );
}

// Native glance header (status/agent/machine/elapsed/tokens/needsHuman)
// wrapping the existing WidgetFrame embed — same mechanism, new skin.

function GlanceCard({
  session,
  live,
  eventsCount,
  elapsed,
  tokens,
  tokensTitle,
  widgetData,
  jwt,
}: {
  session: Session;
  live: boolean;
  eventsCount: number;
  elapsed: string | null;
  tokens: string | null;
  tokensTitle?: string;
  widgetData: Record<string, unknown> | null;
  jwt: string | null;
}) {
  return (
    <div className="sv-glance">
      <div className="sv-glance-head">
        <SwarmMoon size={16} />
        <span className="mono sv-glance-agent">{session.agent}</span>
        <span className="sv-glance-pill">{sessionPill(session)}</span>
      </div>
      <div className="sv-glance-body">
        <div className="mono sv-glance-meta">
          <span>{session.machineId}</span>
          <span>
            {eventsCount} event{eventsCount === 1 ? "" : "s"}
          </span>
          {elapsed && <span>{live ? `${elapsed} · live` : `ended · ${elapsed}`}</span>}
          {tokens && <span title={tokensTitle}>{tokens}</span>}
        </div>
        {session.needsHuman && (
          <div className="sv-glance-needs">
            <StatusPill status="failed">needs you</StatusPill>
          </div>
        )}
        {session.needsHuman && live && (
          <NeedsHumanBar sessionId={session.sessionId} screen={session.needsHumanScreen} />
        )}
        {live && (
          <div className="sv-glance-progress">
            <div className="sv-scan">
              <span className="sv-scanbar" />
            </div>
            <span className="mono sv-scan-label">working…</span>
          </div>
        )}
      </div>
      {widgetData && <WidgetFrame widget="session-status" data={widgetData} jwt={jwt} />}
    </div>
  );
}

// ─── page ───────────────────────────────────────────────────────────

export default function SessionView() {
  const { id } = useParams<{ id: string }>();
  const { status, jwt } = useAuth();
  const [session, setSession] = useState<Session | null>(null);
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const lastSeq = useRef(0);

  // composer (bottom of the transcript column — live sessions only)
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [delivered, setDelivered] = useState(false);

  // stop button (running sessions only)
  const [stopping, setStopping] = useState(false);
  const [stopErr, setStopErr] = useState<string | null>(null);

  const desktop = useDesktop();

  useEffect(() => {
    if (status !== "signed-in" || !id) return;
    let stop = false;
    const tick = async () => {
      try {
        const [s, ev] = await Promise.all([
          getSession(id),
          getSessionEvents(id, lastSeq.current),
        ]);
        if (stop) return;
        setSession(s);
        if (ev.events.length > 0) {
          setEvents((prev) => [...prev, ...ev.events]);
          lastSeq.current = ev.lastSeq;
          setDelivered(false); // the reply is streaming in
        }
        setErr(null);
      } catch (e) {
        if (!stop) setErr(e instanceof Error ? e.message : "failed");
      }
    };
    tick();
    const iv = setInterval(tick, POLL_MS);
    return () => {
      stop = true;
      clearInterval(iv);
    };
  }, [status, id]);

  const live = session?.status === "starting" || session?.status === "running";

  // Elapsed ticks every second while live; frozen at endedAt afterwards.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [live]);
  const elapsed = session
    ? fmtElapsed(session.startedAt, session.endedAt ?? null, live, now)
    : null;

  // Phase 5: compact cumulative token usage for the header; the title
  // carries the full breakdown including cache reads/writes.
  const tokens = session ? tokenSummary(session) : null;
  const tokensTitle = session
    ? `input ${(session.inputTokens ?? 0).toLocaleString()} · output ${(
        session.outputTokens ?? 0
      ).toLocaleString()} · cache read ${(
        session.cacheReadTokens ?? 0
      ).toLocaleString()} · cache write ${(
        session.cacheWriteTokens ?? 0
      ).toLocaleString()}`
    : undefined;

  // Refreshed every poll tick; WidgetFrame dedupes unchanged payloads.
  const widgetData = useMemo(
    () => (session ? buildSessionStatusData(session, events) : null),
    [session, events]
  );

  const rows = useMemo(() => buildRows(events), [events]);

  const toolsSummary = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows)
      if (r.kind === "tool" && r.name) counts.set(r.name, (counts.get(r.name) ?? 0) + 1);
    const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
    if (top.length === 0) return null;
    return top.map(([n, c]) => (c > 1 ? `${n} ×${c}` : n)).join(" · ");
  }, [rows]);

  // Transcript scrolls internally; stick to the bottom while the reader
  // is already there (live tail), stay put when they scrolled up.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickRef = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [rows.length, desktop]);
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending || !id || !live) return;
    setSending(true);
    setSendErr(null);
    setDelivered(false);
    try {
      await sendSessionMessage(id, t);
      setText("");
      setDelivered(true);
    } catch (e2) {
      setSendErr(e2 instanceof Error ? e2.message : "send failed");
    } finally {
      setSending(false);
    }
  };

  const stopRunning = async () => {
    if (!id || stopping) return;
    setStopping(true);
    setStopErr(null);
    try {
      await stopSession(id);
      // stay in "Stopping…" until polling flips the status off "running"
    } catch (e2) {
      setStopErr(e2 instanceof Error ? e2.message : "stop failed");
      setStopping(false);
    }
  };

  const agentLabel = session?.agent ?? "agent";
  const endedTime = session?.endedAt ? fmtTime(session.endedAt) : null;

  const glance = session && (
    <div>
      <div className="mono sv-pin-label">
        <Icon d={I.dot} size={11} /> pinned · at a glance
      </div>
      <GlanceCard
        session={session}
        live={live}
        eventsCount={events.length}
        elapsed={elapsed}
        tokens={tokens}
        tokensTitle={tokensTitle}
        widgetData={widgetData}
        jwt={jwt}
      />
    </div>
  );

  const detailRows: [string, string][] = [];
  if (session) {
    detailRows.push(["agent", session.agent]);
    detailRows.push(["machine", session.machineId]);
    detailRows.push(["status", session.status]);
    const started = fmtWhen(session.startedAt);
    if (started) detailRows.push(["started", started]);
    const ended = fmtWhen(session.endedAt);
    if (ended) detailRows.push(["ended", ended]);
    detailRows.push(["events", String(events.length)]);
    if (session.exitCode !== undefined) detailRows.push(["exit", String(session.exitCode)]);
    if (tokens) detailRows.push(["tokens", tokens]);
    const cacheR = session.cacheReadTokens ?? 0;
    const cacheW = session.cacheWriteTokens ?? 0;
    if (cacheR > 0 || cacheW > 0)
      detailRows.push(["cache", `${compactCount(cacheR)} r / ${compactCount(cacheW)} w`]);
    if (toolsSummary) detailRows.push(["tools", toolsSummary]);
  }

  const legend: [string, RowKind][] = [
    ["you", "user"],
    [agentLabel, "assistant"],
    ["tool", "tool"],
    ["thinking", "thinking"],
    ["system", "system"],
  ];

  return (
    <div className={"sv" + (session && !live ? " sv--ended" : "")}>
      <header className="sv-header">
        <Link to="/dashboard" className="sv-back" aria-label="Back to dashboard">
          <Icon d={I.chevL} size={15} />
        </Link>
        <SwarmMoon size={18} />
        <span className="mono sv-agent">{agentLabel}</span>
        {session && sessionPill(session)}
        <span className="mono sv-sub">
          {session
            ? `${session.machineId}${session.exitCode !== undefined ? ` · exit ${session.exitCode}` : ""}`
            : ""}
        </span>
        {tokens && (
          <span className="mono sv-tokens" title={tokensTitle}>
            {tokens}
          </span>
        )}
        {session?.needsHuman && <StatusPill status="failed">needs you</StatusPill>}
        {session?.status === "running" && (
          <Btn variant="danger" size="sm" onClick={stopRunning} disabled={stopping}>
            <Icon d={I.stop} size={13} /> {stopping ? "Stopping…" : "Stop"}
          </Btn>
        )}
      </header>

      <div className="sv-body">
        {desktop && (
          <aside className="sv-rail">
            {glance}
            {session && (
              <div>
                <div className="mono sv-pin-label">details</div>
                <div className="sv-details">
                  {detailRows.map(([k, v]) => (
                    <div key={k} className="sv-detail-row">
                      <span className="k">{k}</span>
                      <span className="v">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="mono sv-pin-label">row types</div>
              <div className="sv-legend">
                {legend.map(([l, kind]) => (
                  <div key={kind} className="sv-legend-row">
                    <span
                      className="sv-legend-swatch"
                      style={{ background: ROWMETA[kind].spine }}
                    />
                    <span className="sv-legend-label">{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        )}

        <div className="sv-col">
          {!desktop && session && <div className="sv-pinned">{glance}</div>}

          <div className="sv-scroll" ref={scrollRef} onScroll={onScroll}>
            <div className="sv-tx">
              {(err || stopErr || (session?.status === "error" && session.errorMessage)) && (
                <div className="sv-hints">
                  {err && <p className="hint">Couldn't load session ({err})</p>}
                  {stopErr && <p className="hint-error">Couldn't stop session ({stopErr})</p>}
                  {session?.status === "error" && session.errorMessage && (
                    <p className="hint-error mono">{session.errorMessage}</p>
                  )}
                </div>
              )}

              <div className="sv-tx-label">
                <span>transcript · forensic detail</span>
                <span className="sv-tx-rule" />
              </div>

              {rows.length === 0 ? (
                <p className="hint">No events yet…</p>
              ) : (
                rows.map((r, i) => (
                  <TxRow
                    key={r.key}
                    row={r}
                    agent={agentLabel}
                    last={i === rows.length - 1}
                    working={live}
                  />
                ))
              )}

              {session &&
                (live ? (
                  <div className="sv-live-note">
                    <StatusDot tone="live" pulse size={6} /> {agentLabel} is working — stream
                    live
                  </div>
                ) : (
                  <div className="sv-row-system">
                    <span>
                      <span className="sv-sys-rule" />
                      <span className="sv-sys-text">
                        session {session.status === "error" ? "error" : "ended"}
                        {endedTime ? ` · ${endedTime}` : ""}
                      </span>
                      <span className="sv-sys-rule" />
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {session && (
            <form className="sv-composer" onSubmit={send}>
              <div className="sv-composer-inner">
                {live ? (
                  <>
                    <div className="sv-composer-pill">
                      <input
                        className="sv-composer-input"
                        placeholder={`Send a message to ${session.agent}…`}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        disabled={sending}
                      />
                      <button
                        className="sv-composer-send"
                        type="submit"
                        aria-label={sending ? "Sending…" : "Send"}
                        disabled={sending || !text.trim()}
                      >
                        <Icon d={I.send} size={15} />
                      </button>
                    </div>
                    {(sendErr || delivered) && (
                      <div className="mono sv-composer-hint">
                        {sendErr ? (
                          <span className="sv-err">{sendErr}</span>
                        ) : (
                          <span>delivered — reply will stream in</span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="sv-composer-ended">
                    <Icon d={I.power} size={14} /> session ended — message its agent from the
                    dashboard to start a new one
                  </div>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
